import { registerSW } from 'virtual:pwa-register'

/** SW スクリプトの更新チェック間隔 */
const UPDATE_CHECK_INTERVAL_MS = 60_000

/** 新しい SW の install 完了を待つ上限（この時間を超えたら諦める） */
const INSTALL_TIMEOUT_MS = 30_000

export type UpdateCheckResult = 'updated' | 'latest'

let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null
let swRegistration: ServiceWorkerRegistration | null = null

/**
 * SW スクリプト自体を no-store で取り直してから update() を呼ぶ。
 * ブラウザの HTTP キャッシュに阻まれて更新が検知されないのを防ぐ。
 */
const refreshRegistration = async (swUrl: string, registration: ServiceWorkerRegistration): Promise<void> => {
  if (registration.installing) return
  if (!navigator.onLine) return

  const resp = await fetch(swUrl, {
    cache: 'no-store',
    headers: { 'cache-control': 'no-cache' },
  })
  if (resp.status !== 200) return

  await registration.update()
}

/** install 中の SW が installed / activated / redundant になるまで待つ */
const waitForInstalled = (sw: ServiceWorker): Promise<void> =>
  new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer)
      sw.removeEventListener('statechange', onStateChange)
      resolve()
    }
    const onStateChange = () => {
      if (sw.state === 'installing') return
      finish()
    }
    const timer = setTimeout(finish, INSTALL_TIMEOUT_MS)
    sw.addEventListener('statechange', onStateChange)
  })

/** PWA の Service Worker を登録し、定期的に更新チェックを行う */
export const setupPwaUpdate = (): void => {
  updateSW = registerSW({
    immediate: true,
    onOfflineReady() {
      console.log('[PWA] オフライン利用の準備が完了しました')
    },
    onRegisteredSW(swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      if (!registration) return
      swRegistration = registration

      setInterval(() => {
        refreshRegistration(swUrl, registration).catch((err: unknown) =>
          console.error('[PWA] 更新チェックに失敗:', err),
        )
      }, UPDATE_CHECK_INTERVAL_MS)
    },
  })
}

/**
 * 手動での更新チェック。
 * 新バージョンがある場合は install の完了を待ってから有効化し、ページをリロードする。
 * install 完了を待たずにリロードすると、古い SW のまま起動して更新が反映されない。
 */
export const checkForUpdate = async (): Promise<UpdateCheckResult> => {
  const registration = swRegistration ?? (await navigator.serviceWorker?.getRegistration()) ?? null
  if (!registration) return 'latest'

  await registration.update()

  if (registration.installing) await waitForInstalled(registration.installing)

  if (!registration.waiting) return 'latest'

  // waiting 中の SW に SKIP_WAITING を送って有効化し、制御が切り替わったらリロードする
  if (!updateSW) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    window.location.reload()
    return 'updated'
  }

  await updateSW(true)
  return 'updated'
}
